import React, { FC, useState } from 'react';
import cx from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import useCommonStyles from 'styles/common';

import banner from 'assets/banner/banner.png';
import bannerMobile from 'assets/banner/banner-mobile.png';
import masaImg from 'assets/banner/masa.png';
import redImg from 'assets/banner/option-red.png';
import whiteImg from 'assets/banner/option-white.png';
import voteButtonImg from 'assets/banner/vote-btn.svg';
import voteButtonActiveImg from 'assets/banner/vote-btn-active.svg';

import CenterImage from 'components/common/CenterImage';
import VoteCounter from 'components/common/VoteCounter';
import ImageButton from 'components/common/ImageButton';
import CommonModal from 'components/common/CommonModal';
import VoteModalContent from './VoteModalContent';
import { ApiResponse, VoteFormValues } from 'types';
import { VoteOption } from 'constants/enums';
import CustomAlert from 'components/common/CustomAlert';


const useStyles = makeStyles((theme) => ({
    bannerSection: { 
        position: 'relative',
    },
    topBanner: {
        position: 'absolute',
        marginTop: 'calc(-100vw / 1440 * 341 * 0.23)', // 1440時是342*0.23
        [theme.breakpoints.down('xs')]: {
            display: 'none'
        },
    },
    topBannerMobile: {
        display: 'none',
        position: 'absolute',
        marginTop: 'calc(-100vw / 375 * 190 * 0.42)', // 375時是 190px中的80 => 0.42
        [theme.breakpoints.down('xs')]: {
            display: 'block'
        },
    },
    masaOverlay: {
        position: 'absolute',
        bottom: 0,
        maxWidth: 406,
        width: 'min(406px, 27vw)',
        zIndex: 1,
        [theme.breakpoints.down('xs')]: {
            width: 138,
            top: 134,
            bottom: 'unset'
        }
    },
    voteOptionsContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
    },
    voteOption: {
        flex: 1,
        padding: 20,
        paddingTop: 'calc(100vw / 1440 * 341 * 0.48 + clamp(20px, 4vw, 58px))',
        paddingBottom: 47,
        [theme.breakpoints.down('xs')]: {
            paddingTop: 'calc(100vw / 375 * 190 * 0.57 + 24px)', // 375時是 190px中的110 => 0.謢
        },
    },
    voteOptionLeft: {
        backgroundColor: 'var(--color-primary)',
        color: 'var(--color-white)',
        alignItems: 'end'
    },
    voteOptionRight: {
        backgroundColor: 'var(--color-secondary)',
        color: 'var(--color-tertiary)',
        alignItems: 'start'

    },

    optionWrap: { // 包含標題/ 圖片/ 票數/ 投票按鈕
        maxWidth: 410,
    },
    optionWrapLeft: { 
        transform: 'translateX(clamp(-50px, -2.5vw, 0px))',
        marginRight: 'calc(min(10vw, 144px))',
        '@media (max-width: 1200px)': {
            transform: 'translateX(clamp(-20px, -1vw, 0px))',
        },
        '@media (max-width: 1100px)': {
            transform: 'translateX(clamp(-20px, -0.5vw, 0px))',
        },
        '@media (max-width: 1000px)': {
            transform: 'none'
        },
        [theme.breakpoints.down('xs')]: {
            transform: 'none',
            marginRight: 0
        },
    },
    optionWrapRight: { 
        marginLeft: 'calc(min(10vw, 144px))',
        [theme.breakpoints.down('xs')]: {
            marginLeft: 0
        },
    },
    optionTitle: {
        marginBottom: 30,
        '@media (max-width: 900px)': {
            fontSize: 24,
            marginBottom: 24
        },
        [theme.breakpoints.down('xs')]: {
            marginBottom: 16,
            fontSize: 16
        },

    },
    optionCount: {
        marginTop: 42,
        marginBottom: 36,
        [theme.breakpoints.down('xs')]: {
            marginTop: 80,
            marginBottom: 16
        },
    }
}));


interface BannerSectionProps {
    redCount?: number;
    whiteCount?: number;
    submitVoteForm: (form: {}) => Promise<ApiResponse>;
    voteRef?: React.RefObject<HTMLDivElement>;
}

const BannerSection: FC<BannerSectionProps> = ({ redCount, whiteCount, submitVoteForm, voteRef}) => {
    const classes = useStyles();
    const common = useCommonStyles();

    const [ isVoteModalOpen, setIsVoteModalOpen ] = useState(false);
    const [ voteFormValues, setVoteFormValues ] = useState<VoteFormValues>({});
    
    const [ alertMessage, setAlertMessage] = useState('');
    const closeAlert = () => {
        setAlertMessage('');
    }
    
    const setVoteFormValueByKey = (key: string, value: any) => {
        setVoteFormValues({
            ...voteFormValues,
            [key]: value
        });
    }

    const openRedModal = () => {
        setIsVoteModalOpen(true);
        setVoteFormValues({ option: VoteOption.RED });
    }

    const openWhiteModal = () => {
        setIsVoteModalOpen(true);
        setVoteFormValues({ option: VoteOption.WHITE });
    }

    // NOTE 檢查的部分在submitVoteForm
    const onSubmit = async () => {
        const result = await submitVoteForm(voteFormValues);
        if (!result.isSent) { // ApiResponse 
            setAlertMessage(result.message || '欄位錯誤');
            return; // 表單內容檢查未通過
        } else if (result.message?.includes('Duplicate')) {
            setAlertMessage('每人每日限投一票！');
        } else if (result.isSuccess) {
            setAlertMessage('投票完成！');
            setIsVoteModalOpen(false);
            setVoteFormValues({});
        } else {
            setAlertMessage('投票失敗，請重新嘗試！');
        }
    }

    return (<div className={ cx(common.columnCenter, classes.bannerSection) }>
		<CustomAlert open={!!alertMessage} message={ alertMessage } onClose={closeAlert} />
		<CenterImage src={banner} alt='banner' className={ cx(common.fullWidth, classes.topBanner) }/>
		<CenterImage src={bannerMobile} alt='banner' className={ cx(common.fullWidth, classes.topBannerMobile) }/>
        <div className={ cx(classes.masaOverlay) } >
            <CenterImage src={masaImg} alt='masa' className={ common.imgFill }/>
        </div>
        <div className={ cx(classes.voteOptionsContainer, common.fullWidth) } ref={voteRef}>
            <div className={ cx(classes.voteOption, classes.voteOptionLeft, common.columnCenter) }>
                <div className={ cx(common.columnCenter, classes.optionWrap, classes.optionWrapLeft) }>
                    <Typography 
                        className={ classes.optionTitle } 
                        variant="subtitle1" 
                        style={{ color: 'var(--color-pink)' }}
                    >{"蕃茄燴飯料理塊"}</Typography>
                    <CenterImage src={redImg} alt='red'/>
                    <VoteCounter 
                        voteCount={redCount ?? 0} 
                        className={cx(classes.optionCount, common.fullWidth)} 
                        fontStyle={{color:'var(--color-white)'}}
                    />
                    <ImageButton 
                        btnImg={ voteButtonImg }
                        btnImgActive={ voteButtonActiveImg }
                        maxWidth={ 275 }
                        onClick={openRedModal}
                    />
                </div>

            </div>
            <div className={ cx(classes.voteOption, classes.voteOptionRight, common.columnCenter) }>
                <div className={ cx(common.columnCenter, classes.optionWrap, classes.optionWrapRight) }>
                    <Typography 
                        className={ classes.optionTitle } 
                        variant="subtitle1" 
                    >{"北海道白醬料理塊"}</Typography>
                    <CenterImage src={whiteImg} alt='white'/>
                    <VoteCounter 
                        voteCount={whiteCount ?? 0} 
                        className={cx(classes.optionCount, common.fullWidth)} 
                        fontStyle={{color:'var(--color-tertiary)'}}
                    />
                    <ImageButton 
                        btnImg={ voteButtonImg }
                        btnImgActive={ voteButtonActiveImg }
                        maxWidth={ 275 }
                        onClick={openWhiteModal}
                    />
                </div>

            </div>
        </div>

		<CommonModal isOpen={isVoteModalOpen} handleClose={() => setIsVoteModalOpen(false)}>
			<VoteModalContent 
				option={voteFormValues?.option}
				values={voteFormValues}
				setValueByKey={setVoteFormValueByKey}
				onSubmit={onSubmit}
			/>
		</CommonModal>
    </div>);
};

export default BannerSection;
